import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import { omit } from 'lodash';
import JSZip from 'jszip';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core';

import Export from '../../../services/export';
import { exportJSON } from '../../../common/utils';
import { INTERNALS } from '../../../common/metadata';
import {
  buildShorelinesKMLBody,
  buildReferenceKMLBody,
  buildBaselineKMLBody,
  buildProjectionsKMLBody,
  buildTransectsKMLBody,
} from '../../../algorithms/utils/turf';
import { shapeTransectData, buildGeoJsonData, sortOutput } from './transectExportUtils';

/**
 * @function stripInternals
 * @description Removes the internal `INTERNALS` metadata property from a feature's properties,
 * so it isn't leaked into exported files.
 * @param {Object} feature - GeoJSON feature to sanitize.
 * **Called by:
 * @see {exportGeoref}
 * @memberof module:ShoreR
 * @returns {Object} The feature, with `INTERNALS` removed from its properties.
 */
const stripInternals = (feature) => ({
  ...feature,
  properties: omit(feature?.properties || {}, [INTERNALS]),
});

/**
 * @function buildProjectionFeatures
 * @description Builds one LineString feature per contiguous same-label run of each horizon's
 * projected points, tagged with the horizon and erosion label.
 * @param {Array<{horizon: Number, points: Array<Object>}>} projections - Computed projections, per horizon.
 * @param {Array<Object>} transectData - Transect features, used to resolve each point's erosion label.
 * **Called by:
 * @see {exportGeoref}
 * @memberof module:ShoreR
 * @returns {Array<Object>} GeoJSON LineString features, one per label run per horizon.
 */
const buildProjectionFeatures = (projections, transectData) => {
  const labelById = {};
  (transectData || []).forEach((f) => {
    if (f.id != null) labelById[f.id] = f.properties?.label ?? null;
  });

  return (projections || []).flatMap(({ horizon, points }) => {
    const features = [];
    let segmentPoints = [];
    let currentLabel = null;

    points.forEach((pt) => {
      const label = labelById[pt.id] ?? null;
      if (label !== currentLabel) {
        if (segmentPoints.length >= 2) {
          features.push({
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: segmentPoints.map((p) => [p.lng, p.lat]) },
            properties: { horizon_in_yrs: horizon, label: currentLabel },
          });
        }
        currentLabel = label;
        segmentPoints = [pt];
      } else {
        segmentPoints.push(pt);
      }
    });

    if (segmentPoints.length >= 2) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: segmentPoints.map((p) => [p.lng, p.lat]) },
        properties: { horizon_in_yrs: horizon, label: currentLabel },
      });
    }

    return features;
  });
};

/**
 * @function buildBaselineFeature
 * @description Wraps baseline coordinates as a GeoJSON LineString feature.
 * @param {Array<Array<Number>>} coords - Baseline coordinates as [lng, lat] pairs.
 * **Called by:
 * @see {exportGeoref}
 * @memberof module:ShoreR
 * @returns {Object|null} The baseline LineString feature, or null if no coordinates are given.
 */
const buildBaselineFeature = (coords) =>
  coords?.length
    ? {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: {},
      }
    : null;

/**
 * @function buildRatesRows
 * @description Builds one CSV/JSON row per transect with its shoreline change statistics.
 * @param {Array<Object>} transectData - Transect features with rate properties.
 * **Called by:
 * @see {exportNonGeoref}
 * @memberof module:ShoreR
 * @returns {Array<Object>} One row per transect: id, slope, intercept, lrr, epr, rsquared, sce, nsm, smoothed_lrr, smoothed_epr, label.
 */
const buildRatesRows = (transectData) =>
  transectData.map((f) => {
    const p = f.properties || {};
    return {
      id: parseInt(f.id, 10),
      slope: p.slope,
      intercept: p.intercept,
      lrr: p.lrr,
      epr: p.epr,
      rsquared: p.rsquared,
      sce: p.sce,
      nsm: p.nsm,
      smoothed_lrr: p.smoothed_lrr,
      smoothed_epr: p.smoothed_epr,
      label: p.label,
    };
  });

/**
 * @function buildExtrapolationsRows
 * @description Flattens every horizon's projected points into one CSV/JSON row per point.
 * @param {Array<{horizon: Number, points: Array<Object>}>} projections - Computed projections, per horizon.
 * **Called by:
 * @see {exportNonGeoref}
 * @memberof module:ShoreR
 * @returns {Array<Object>} One row per projected point: id, horizon_in_yrs, lat, lng.
 */
const buildExtrapolationsRows = (projections) =>
  (projections || []).flatMap(({ horizon, points }) =>
    (points || []).map((pt) => ({
      id: pt.id,
      horizon_in_yrs: horizon,
      lat: pt.lat,
      lng: pt.lng,
    })),
  );

/**
 * @function buildReferenceExportFeature
 * @description Builds the exportable reference-shoreline feature, tagging it with the source
 * date(s) it was derived from: the trailing-year window of dates for the "yearlyMean" mode, or
 * the single reference shoreline's date otherwise.
 * @param {Object} referenceFeature - Reference shoreline feature used for the projections.
 * @param {String} referenceMode - Reference mode ("latest", "byDate", or "yearlyMean").
 * @param {Object} shorelinesGeoJSON - GeoJSON FeatureCollection of shorelines.
 * **Called by:
 * @see {exportGeoref}
 * @memberof module:ShoreR
 * @returns {Object|null} The reference feature tagged with `from_dates`, or null if there's no reference geometry.
 */
const buildReferenceExportFeature = (referenceFeature, referenceMode, shorelinesGeoJSON) => {
  if (!referenceFeature?.geometry) return null;

  let from_dates = [];
  if (referenceMode === 'yearlyMean') {
    const dates = (shorelinesGeoJSON?.features || [])
      .map((f) => f.properties?.date)
      .filter(Boolean)
      .sort();
    if (dates.length) {
      const cutoff = new Date(dates[dates.length - 1]);
      cutoff.setFullYear(cutoff.getFullYear() - 1);
      from_dates = dates.filter((d) => new Date(d) >= cutoff);
    }
  } else {
    from_dates = [referenceFeature.properties?.date].filter(Boolean);
  }

  return {
    type: 'Feature',
    geometry: referenceFeature.geometry,
    properties: { from_dates },
  };
};

/**
 * @function downloadBlob
 * @description Creates a temporary anchor element to trigger a browser download of a Blob.
 * @param {Blob} blob - Blob to download.
 * @param {String} filename - Filename for the downloaded file.
 * **Called by:
 * @see {downloadKMLString}
 * @see {exportNonGeoref}
 * @see {zipAndDownload}
 * @memberof module:ShoreR
 * @returns {void}
 */
const downloadBlob = (blob, filename) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

/**
 * @function downloadKMLString
 * @description Wraps a KML string as a Blob with the correct MIME type and triggers its download.
 * @param {String} kml - Complete KML document string.
 * @param {String} filename - Filename for the downloaded file.
 * @see {@link downloadBlob}
 * **Called by:
 * @see {exportGeoref}
 * @memberof module:ShoreR
 * @returns {void}
 */
const downloadKMLString = (kml, filename) => {
  const blob = new Blob([kml], {
    type: 'application/vnd.google-earth.kml+xml',
  });
  downloadBlob(blob, filename);
};

/**
 * @function wrapKMLDocument
 * @description Wraps a KML body in the standard XML declaration and `<kml><Document>` envelope.
 * @param {String} body - Inner KML document body (placemarks, etc.).
 * **Called by:
 * @see {exportGeoref}
 * @memberof module:ShoreR
 * @returns {String} The complete KML document string.
 */
const wrapKMLDocument = (body) =>
  `<?xml version="1.0" encoding="UTF-8"?>
  <kml xmlns="http://www.opengis.net/kml/2.2">
    <Document>${body}</Document>
  </kml>`;

/**
 * @function zipAndDownload
 * @description Zips a set of in-memory files and triggers the archive's download.
 * @param {Array<{name: String, content: String}>} files - Files to include in the archive.
 * @param {String} zipName - Filename for the downloaded zip.
 * @see {@link downloadBlob}
 * **Called by:
 * @see {exportNonGeoref}
 * @see {exportGeoref}
 * @memberof module:ShoreR
 * @returns {Promise<void>}
 */
const zipAndDownload = async (files, zipName) => {
  const zip = new JSZip();
  files.forEach(({ name, content }) => zip.file(name, content));
  const blob = await zip.generateAsync({ type: 'blob' });
  downloadBlob(blob, zipName);
};

/**
 * @function ShoreRExportDialog
 * @description Dialog for exporting ShoreR analysis results, either as non-georeferenced
 * data (coastline analysis, rates, extrapolations — as CSV/JSON) or as georeferenced layers
 * (shorelines, reference shoreline, baseline, future projections, transects — as Shapefile/KML/GeoJSON).
 * @param {Object} props - Component properties.
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {Function} props.close - Called to close the dialog.
 * @see {@link exportNonGeoref} - handles the non-georeferenced tab's exports.
 * @see {@link exportGeoref} - handles the georeferenced tab's exports.
 * **Called by:
 * @see {ShoreRAnalysisSteps}
 * @memberof module:ShoreR
 * @returns {JSX.Element} The export options dialog.
 */
const ShoreRExportDialog = ({ open, close }) => {
  const [t] = useTranslation();
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const transectData = useSelector((s) => s.results.shoreRData?.transectData || []);
  const shorelinesGeoJSON = useSelector((s) => s.results.shoreRData?.shorelinesGeoJSON);
  const baselineCoords = useSelector((s) => s.results.baselineData?.coords || []);
  const shoreRProjections = useSelector((s) => s.results.shoreRProjections);

  const projections = shoreRProjections?.projections;
  const referenceFeature = shoreRProjections?.referenceFeature;
  const referenceMode = shoreRProjections?.referenceMode;

  const hasProjections = Boolean(projections?.length);
  const hasReference = Boolean(referenceFeature?.geometry);
  const hasShorelines = Boolean(shorelinesGeoJSON?.features?.length);
  const hasBaseline = baselineCoords.length >= 2;
  const hasTransects = transectData.length > 0;

  const [tab, setTab] = useState(0);

  const [nonGeo, setNonGeo] = useState({
    coastlineAnalysis: true,
    rates: true,
    extrapolations: false,
  });
  const [geo, setGeo] = useState({
    shorelines: true,
    reference: false,
    baseline: true,
    future: false,
    transects: true,
  });

  /**
   * @function toggleNonGeo
   * @description Returns a handler that toggles a non-georeferenced export option by key.
   * @param {String} key - Option key within `nonGeo` to toggle.
   * **Called by:
   * @see {ShoreRExportDialog} - via each non-georeferenced checkbox's onChange.
   * @memberof module:ShoreR
   * @returns {Function} Handler that toggles the given option when invoked.
   */
  const toggleNonGeo = (key) => () => setNonGeo((prev) => ({ ...prev, [key]: !prev[key] }));
  /**
   * @function toggleGeo
   * @description Returns a handler that toggles a georeferenced export layer by key.
   * @param {String} key - Layer key within `geo` to toggle.
   * **Called by:
   * @see {ShoreRExportDialog} - via each georeferenced checkbox's onChange.
   * @memberof module:ShoreR
   * @returns {Function} Handler that toggles the given layer when invoked.
   */
  const toggleGeo = (key) => () => setGeo((prev) => ({ ...prev, [key]: !prev[key] }));

  const nonGeoSelected = useMemo(
    () =>
      Object.entries(nonGeo)
        .filter(([, v]) => v)
        .map(([k]) => k),
    [nonGeo],
  );
  const geoSelected = useMemo(
    () =>
      Object.entries(geo)
        .filter(([, v]) => v)
        .map(([k]) => k),
    [geo],
  );

  /**
   * @function handleClose
   * @description Closes the export dialog.
   * **Called by:
   * @see {ShoreRExportDialog} - via the "Close" button's onClick, and the Dialog's onClose.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const handleClose = () => close();

  /**
   * @function exportNonGeoref
   * @description Builds and downloads the selected non-georeferenced datasets (coastline
   * analysis, rates, extrapolations) as CSV or JSON, zipping them together if more than one is
   * selected.
   * @param {String} format - "csv" or "json".
   * @see {@link buildRatesRows}
   * @see {@link buildExtrapolationsRows}
   * @see {@link csvContent}
   * @see {@link zipAndDownload}
   * **Called by:
   * @see {ShoreRExportDialog} - via the "CSV"/"JSON" buttons' onClick.
   * @memberof module:ShoreR
   * @returns {Promise<void>}
   */
  const exportNonGeoref = async (format) => {
    if (!nonGeoSelected.length) {
      notifyError(t('forms.shoreR.exports.empty'));
      return;
    }

    const files = [];

    if (nonGeo.coastlineAnalysis && hasTransects) {
      if (format === 'csv') {
        const rows = sortOutput(shapeTransectData(transectData));
        files.push({
          name: 'analise-linha-costa.csv',
          content: csvContent(rows),
        });
      } else {
        const data = buildGeoJsonData(shapeTransectData(transectData));
        files.push({
          name: 'analise-linha-costa.json',
          content: JSON.stringify(data),
        });
      }
    }

    if (nonGeo.rates && hasTransects) {
      const rows = buildRatesRows(transectData);
      if (format === 'csv') {
        files.push({ name: 'taxas.csv', content: csvContent(rows) });
      } else {
        files.push({ name: 'taxas.json', content: JSON.stringify(rows) });
      }
    }

    if (nonGeo.extrapolations && hasProjections) {
      const rows = buildExtrapolationsRows(projections);
      if (format === 'csv') {
        files.push({
          name: 'extrapolacoes.csv',
          content: csvContent(rows),
        });
      } else {
        files.push({
          name: 'extrapolacoes.json',
          content: JSON.stringify({
            method: shoreRProjections?.method,
            horizons: shoreRProjections?.horizons,
            data: rows,
          }),
        });
      }
    }

    if (!files.length) return;

    if (files.length === 1) {
      const { name, content } = files[0];
      const mime = format === 'csv' ? 'text/csv' : 'application/json';
      downloadBlob(new Blob([content], { type: mime }), name);
      return;
    }
    await zipAndDownload(files, `shoreR-${format}.zip`);
  };

  /**
   * @function exportGeoref
   * @description Builds and downloads the selected georeferenced layers (shorelines, reference,
   * baseline, future projections, transects) in the requested format.
   * 1. Builds each selected layer's features (stripping internal metadata), keeping only layers
   *    that end up with at least one feature.
   * 2. For "shp": exports all layers as a single shapefile group.
   * 3. For "kml": builds each layer's KML body (via `algorithms/utils/turf`'s builders) and
   *    downloads it directly, or zipped if there's more than one.
   * 4. For "geojson": downloads each layer as a FeatureCollection, or zipped if there's more than one.
   * @param {String} format - "shp", "kml", or "geojson".
   * @see {@link stripInternals}
   * @see {@link buildReferenceExportFeature}
   * @see {@link buildBaselineFeature}
   * @see {@link buildProjectionFeatures}
   * @see {@link wrapKMLDocument}
   * @see {@link downloadKMLString}
   * @see {@link zipAndDownload}
   * **Called by:
   * @see {ShoreRExportDialog} - via the "Shapefile"/"KML"/"GeoJSON" buttons' onClick.
   * @memberof module:ShoreR
   * @returns {Promise<void>}
   */
  const exportGeoref = async (format) => {
    if (!geoSelected.length) {
      notifyError(t('forms.shoreR.exports.empty'));
      return;
    }

    const layers = [];
    if (geo.shorelines && hasShorelines) {
      layers.push({
        name: 'linhas-de-costa',
        features: shorelinesGeoJSON.features.map(stripInternals),
        kmlKey: 'shorelines',
      });
    }
    if (geo.reference && hasReference) {
      const refExportFeature = buildReferenceExportFeature(referenceFeature, referenceMode, shorelinesGeoJSON);
      layers.push({
        name: 'linha-referencia',
        features: refExportFeature ? [refExportFeature] : [],
        kmlKey: 'reference',
      });
    }
    if (geo.baseline && hasBaseline) {
      const f = buildBaselineFeature(baselineCoords);
      layers.push({
        name: 'linha-base',
        features: f ? [f] : [],
        kmlKey: 'baseline',
      });
    }
    if (geo.future && hasProjections) {
      layers.push({
        name: 'linhas-futuras',
        features: buildProjectionFeatures(projections, transectData),
        kmlKey: 'future',
      });
    }
    if (geo.transects && hasTransects) {
      layers.push({
        name: 'transectos',
        features: transectData.map(stripInternals),
        kmlKey: 'transects',
      });
    }

    const validLayers = layers.filter((l) => l.features.length > 0);
    if (!validLayers.length) return;

    if (format === 'shp') {
      Export.table.toDevice.asShapefileGroup(
        validLayers.map((l) => ({ features: l.features })),
        Export.defaultOptions.device.shapefileGroup('shoreR-export', ...validLayers.map((l) => l.name)),
      );
      return;
    }

    if (format === 'kml') {
      const labels = {
        shorelines: t('forms.shoreR.exports.georef.shorelines'),
        reference: t('forms.shoreR.exports.georef.reference'),
        baseline: t('forms.shoreR.exports.georef.baseline'),
        future: t('forms.shoreR.exports.georef.future'),
        transects: t('forms.shoreR.exports.georef.transects'),
      };

      const kmlFiles = validLayers
        .map((layer) => {
          let body = '';
          if (layer.kmlKey === 'shorelines') {
            body = buildShorelinesKMLBody(layer.features, labels.shorelines);
          } else if (layer.kmlKey === 'reference') {
            body = buildReferenceKMLBody(layer.features[0], labels.reference);
          } else if (layer.kmlKey === 'baseline') {
            body = buildBaselineKMLBody(baselineCoords, labels.baseline);
          } else if (layer.kmlKey === 'future') {
            body = buildProjectionsKMLBody(projections, labels.future);
          } else if (layer.kmlKey === 'transects') {
            body = buildTransectsKMLBody(layer.features, labels.transects);
          }
          return body ? { name: `${layer.name}.kml`, content: wrapKMLDocument(body) } : null;
        })
        .filter(Boolean);

      if (!kmlFiles.length) return;

      if (kmlFiles.length === 1) {
        const { name, content } = kmlFiles[0];
        downloadKMLString(content, name);
        return;
      }
      await zipAndDownload(kmlFiles, 'shoreR-kml.zip');
      return;
    }

    if (format === 'geojson') {
      if (validLayers.length === 1) {
        const layer = validLayers[0];
        const fc = {
          type: 'FeatureCollection',
          crs: { type: 'name', properties: { name: 'EPSG:4326' } },
          features: layer.features,
        };
        exportJSON(fc, `${layer.name}.geojson`);
        return;
      }
      const files = validLayers.map((layer) => ({
        name: `${layer.name}.geojson`,
        content: JSON.stringify({
          type: 'FeatureCollection',
          crs: { type: 'name', properties: { name: 'EPSG:4326' } },
          features: layer.features,
        }),
      }));
      await zipAndDownload(files, 'shoreR-geojson.zip');
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth onClose={handleClose}>
      <DialogTitle>{t('forms.shoreR.exports.title')}</DialogTitle>
      <DialogContent>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} indicatorColor="primary" textColor="primary">
          <Tab label={t('forms.shoreR.exports.nonGeoref.title')} />
          <Tab label={t('forms.shoreR.exports.georef.title')} />
        </Tabs>

        {tab === 0 && (
          <Box mt={2}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={nonGeo.coastlineAnalysis}
                    onChange={toggleNonGeo('coastlineAnalysis')}
                    disabled={!hasTransects}
                  />
                }
                label={t('forms.shoreR.exports.nonGeoref.coastlineAnalysis')}
              />
              <FormControlLabel
                control={<Checkbox checked={nonGeo.rates} onChange={toggleNonGeo('rates')} disabled={!hasTransects} />}
                label={t('forms.shoreR.exports.nonGeoref.rates')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={nonGeo.extrapolations}
                    onChange={toggleNonGeo('extrapolations')}
                    disabled={!hasProjections}
                  />
                }
                label={t('forms.shoreR.exports.nonGeoref.extrapolations')}
              />
            </FormGroup>
            <Box mt={2} style={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" style={{ marginRight: 12 }}>
                {t('forms.shoreR.exports.formatLabel')}:
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => exportNonGeoref('csv')}
                style={{ marginRight: 8 }}>
                CSV
              </Button>
              <Button variant="outlined" color="primary" onClick={() => exportNonGeoref('json')}>
                JSON
              </Button>
            </Box>
          </Box>
        )}

        {tab === 1 && (
          <Box mt={2}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox checked={geo.shorelines} onChange={toggleGeo('shorelines')} disabled={!hasShorelines} />
                }
                label={t('forms.shoreR.exports.georef.shorelines')}
              />
              <FormControlLabel
                control={
                  <Checkbox checked={geo.reference} onChange={toggleGeo('reference')} disabled={!hasReference} />
                }
                label={t('forms.shoreR.exports.georef.reference')}
              />
              <FormControlLabel
                control={<Checkbox checked={geo.baseline} onChange={toggleGeo('baseline')} disabled={!hasBaseline} />}
                label={t('forms.shoreR.exports.georef.baseline')}
              />
              <FormControlLabel
                control={<Checkbox checked={geo.future} onChange={toggleGeo('future')} disabled={!hasProjections} />}
                label={t('forms.shoreR.exports.georef.future')}
              />
              <FormControlLabel
                control={
                  <Checkbox checked={geo.transects} onChange={toggleGeo('transects')} disabled={!hasTransects} />
                }
                label={t('forms.shoreR.exports.georef.transects')}
              />
            </FormGroup>
            <Box mt={2} style={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" style={{ marginRight: 12 }}>
                {t('forms.shoreR.exports.formatLabel')}:
              </Typography>
              <Button variant="outlined" color="primary" onClick={() => exportGeoref('shp')} style={{ marginRight: 8 }}>
                Shapefile
              </Button>
              <Button variant="outlined" color="primary" onClick={() => exportGeoref('kml')} style={{ marginRight: 8 }}>
                KML
              </Button>
              <Button variant="outlined" color="primary" onClick={() => exportGeoref('geojson')}>
                GeoJSON
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={handleClose}>
          {t('forms.shoreR.exports.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * @function csvContent
 * @description Serializes an array of flat objects into CSV text, using the first row's keys as
 * the header.
 * @param {Array<Object>} rows - Rows to serialize.
 * **Called by:
 * @see {exportNonGeoref}
 * @memberof module:ShoreR
 * @returns {String} The CSV text, or an empty string if `rows` is empty.
 */
const csvContent = (rows) => {
  if (!rows.length) return '';
  const cols = Object.keys(rows[0]);
  const lines = [cols.join(',')];
  rows.forEach((row) => {
    lines.push(cols.map((c) => row[c]).join(','));
  });
  return lines.join('\n');
};

export default ShoreRExportDialog;
